import React, { useState, useEffect } from 'react';
import { HashRouter, Routes, Route, useNavigate, useParams, useLocation, Navigate } from 'react-router-dom';
import { SERVICE_CATEGORIES } from './data/services';
import { OrderDraft, CartItem, ServiceItem } from './types';
import { CategoryCard } from './components/CategoryCard';
import { ServiceCard } from './components/ServiceCard';
import { StickyButton } from './components/StickyButton';
import { ConfirmationModal } from './components/ConfirmationModal';
import { AddressAutocomplete } from './components/AddressAutocomplete';
import { calculatePriceRange, calculateCartTotal, formatCurrency } from './utils/price';
import { 
  ArrowLeft, 
  ArrowRight, 
  Hammer, 
  HelpCircle, 
  MessagesSquare, 
  PenLine, 
  Search, 
  ShieldCheck, 
  ShoppingBag, 
  Trash2, 
  User, 
  Wallet 
} from 'lucide-react';

// --- Components ---

const SplashScreen = ({ onComplete }: { onComplete: () => void }) => {
  useEffect(() => {
    // Simulate loading delay
    const timer = setTimeout(() => {
      onComplete();
    }, 2500);
    return () => clearTimeout(timer);
  }, [onComplete]);

  return (
    <div className="fixed inset-0 z-[100] bg-white flex flex-col items-center justify-center p-4 overflow-hidden">
      {/* 3D decorative background */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[120%] h-[120%] bg-gradient-to-tr from-blue-50 via-white to-indigo-50 opacity-50" />
      <div className="absolute top-0 right-0 w-64 h-64 bg-blue-100/50 rounded-full blur-3xl -mr-16 -mt-16 animate-pulse" />
      <div className="absolute bottom-0 left-0 w-64 h-64 bg-indigo-100/50 rounded-full blur-3xl -ml-16 -mb-16 animate-pulse" style={{ animationDelay: '1s' }} />

      <div className="relative z-10 flex flex-col items-center animate-zoomIn">
         {/* 3D Logo Block */}
         <div className="relative w-32 h-32 mb-8">
            <div className="absolute inset-0 bg-blue-600 rounded-3xl shadow-xl rotate-6 opacity-20 animate-float" style={{ animationDuration: '4s' }} />
            <div className="absolute inset-0 bg-white rounded-3xl shadow-3d flex items-center justify-center rotate-0 z-10 border border-blue-50">
               {/* Try to load local logo, fallback to icon */}
               <img 
                  src="/assets/images/logo.png" 
                  alt="" 
                  className="w-20 h-20 object-contain"
                  onError={(e) => {
                      // Fallback if image fails (e.g. empty file)
                      e.currentTarget.style.display = 'none';
                      e.currentTarget.nextElementSibling?.classList.remove('hidden');
                  }}
               />
               <div className="w-20 h-20 bg-gradient-to-br from-primary to-blue-600 rounded-2xl flex items-center justify-center shadow-inner hidden">
                  <Hammer className="text-white w-10 h-10 drop-shadow-md" strokeWidth={2.5} />
               </div>
            </div>
         </div>

         <h1 className="text-4xl font-black text-gray-900 tracking-tight text-center leading-tight">
            Мастер<span className="text-primary">Про</span>
         </h1>
         <p className="text-sm font-bold text-gray-400 tracking-[0.4em] uppercase mt-3">
            Сервис РФ
         </p>
      </div>

      <div className="absolute bottom-16 flex flex-col items-center gap-3">
         <div className="flex gap-2">
             <div className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: '0s' }} />
             <div className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: '0.2s' }} />
             <div className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: '0.4s' }} />
         </div>
         <p className="text-xs text-gray-400 font-medium">Загрузка...</p>
      </div>
    </div>
  );
};

const CartButton = ({ itemCount, onClick }: { itemCount: number, onClick: () => void }) => {
  if (itemCount === 0) return null;
  
  return (
    <div className="fixed bottom-6 right-6 z-40 animate-zoomIn">
      <button 
        onClick={onClick}
        className="relative w-16 h-16 bg-gray-900 rounded-2xl shadow-float flex items-center justify-center text-white active:scale-90 transition-transform border border-white/10"
      >
        <ShoppingBag size={28} />
        <div className="absolute -top-2 -right-2 w-7 h-7 bg-primary rounded-full border-2 border-[#F0F2F5] flex items-center justify-center text-xs font-bold shadow-sm">
           {itemCount}
        </div>
      </button>
    </div>
  );
};

const WelcomeScreen = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);

  const completeOnboarding = () => {
    localStorage.setItem('hasSeenOnboarding', 'true');
    navigate('/home');
  };

  const steps = [
    {
      title: "Найдите мастера\nза пару кликов",
      description: "Выберите услугу, укажите время, и мы подберем специалиста рядом с вами.",
      image: "/assets/images/onboarding_search.png",
      icon: <Search className="text-white w-12 h-12" strokeWidth={2} />,
      colorClass: "from-blue-500 to-indigo-600",
      bgClass: "bg-blue-50",
      shadowClass: "shadow-blue-500/30",
    },
    {
      title: "Честные цены\nбез сюрпризов",
      description: "Диапазон стоимости известен заранее. Никаких скрытых наценок после работ.",
      image: "/assets/images/onboarding_prices.png",
      icon: <Wallet className="text-white w-12 h-12" strokeWidth={2} />,
      colorClass: "from-emerald-500 to-teal-600",
      bgClass: "bg-emerald-50",
      shadowClass: "shadow-emerald-500/30",
    },
    {
      title: "Гарантия на\nвсе работы",
      description: "Только проверенные мастера с рейтингом. Мы страхуем каждый заказ.",
      image: "/assets/images/onboarding_guarantee.png",
      icon: <ShieldCheck className="text-white w-12 h-12" strokeWidth={2} />,
      colorClass: "from-orange-400 to-red-500",
      bgClass: "bg-orange-50",
      shadowClass: "shadow-orange-500/30",
    }
  ];

  const handleNext = () => {
    if (step < steps.length - 1) {
      setStep(step + 1);
    } else {
      completeOnboarding();
    }
  };

  const currentStep = steps[step];

  return (
    <div className={`min-h-screen relative overflow-hidden bg-white flex flex-col justify-between p-6 transition-colors duration-500 ${currentStep.bgClass}`}>
      {/* Background Blobs */}
      <div className={`absolute top-[-20%] left-[-20%] w-[80%] h-[50%] rounded-full blur-[80px] opacity-40 transition-colors duration-500 bg-gradient-to-br ${currentStep.colorClass}`} />
      <div className={`absolute bottom-[-10%] right-[-10%] w-[70%] h-[40%] rounded-full blur-[80px] opacity-40 transition-colors duration-500 bg-gradient-to-tr ${currentStep.colorClass}`} />

      {/* Skip button */}
      <div className="w-full flex justify-end z-20">
         <button onClick={completeOnboarding} className="text-gray-400 font-medium text-sm py-2 px-4 hover:bg-black/5 rounded-full transition-colors">
            Пропустить
         </button>
      </div>

      <div className="flex-1 flex flex-col items-center justify-center w-full z-10 -mt-10">
        <div className="relative w-72 h-72 mb-10 animate-float transition-all duration-500">
           <div className={`absolute inset-0 bg-gradient-to-br ${currentStep.colorClass} rounded-[3rem] shadow-2xl ${currentStep.shadowClass} rotate-6 flex items-center justify-center transition-all duration-500`}>
              <div className="absolute inset-0 bg-white/20 blur-xl rounded-[3rem]" />
           </div>
           
           <div className="absolute inset-0 bg-white/30 backdrop-blur-md rounded-[3rem] border border-white/60 -rotate-3 flex flex-col items-center justify-center shadow-glass transition-transform duration-500 overflow-hidden">
               {/* Use Image if valid, else fallback to icon */}
               <img 
                  src={currentStep.image} 
                  alt="" 
                  className="w-full h-full object-cover absolute inset-0 opacity-90"
                  onError={(e) => {
                      e.currentTarget.style.display = 'none';
                      e.currentTarget.nextElementSibling?.classList.remove('hidden');
                  }}
               />
              <div className="hidden flex-col items-center justify-center h-full w-full">
                  <div className={`w-24 h-24 bg-gradient-to-tr ${currentStep.colorClass} rounded-3xl flex items-center justify-center shadow-lg mb-4 transition-all duration-500 ring-4 ring-white/50`}>
                     {currentStep.icon}
                  </div>
                  <div className="w-32 h-3 bg-white/50 rounded-full mb-3" />
                  <div className="w-20 h-3 bg-white/50 rounded-full" />
              </div>
           </div>
        </div>

        <div className="text-center px-4 max-w-sm">
            <h1 className="text-3xl font-extrabold text-gray-900 mb-4 whitespace-pre-line leading-tight transition-all duration-300">
                {currentStep.title}
            </h1>
            <p className="text-gray-500 text-lg leading-relaxed transition-all duration-300 min-h-[5rem]">
                {currentStep.description}
            </p>
        </div>
      </div>

      <div className="w-full z-10 flex flex-col gap-8 mb-4">
        <div className="flex justify-center gap-2">
            {steps.map((_, i) => (
                <div 
                    key={i} 
                    className={`h-2 rounded-full transition-all duration-300 ${i === step ? `w-8 bg-gray-800` : 'w-2 bg-gray-300'}`} 
                />
            ))}
        </div>

        <button 
          onClick={handleNext}
          className="w-full bg-gray-900 text-white font-bold text-lg py-5 rounded-3xl shadow-xl shadow-gray-900/20 active:scale-[0.98] transition-all flex items-center justify-center gap-3 relative overflow-hidden group"
        >
          <span className="relative z-10">{step === steps.length - 1 ? 'Начать пользоваться' : 'Далее'}</span>
          <ArrowRight className="relative z-10 group-hover:translate-x-1 transition-transform" />
          <div className="absolute inset-0 bg-gray-800 opacity-0 group-hover:opacity-100 transition-opacity" />
        </button>
      </div>
    </div>
  );
};

const HomeScreen = ({ cartCount, onCartClick }: { cartCount: number, onCartClick: () => void }) => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 via-[#F0F2F5] to-[#F0F2F5] p-4 pb-24">
      <header className="mb-8 mt-2 px-1">
        <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white rounded-2xl flex items-center justify-center shadow-3d text-primary border border-white/50 overflow-hidden">
                    {/* Logo in header */}
                    <img 
                       src="/assets/images/logo.png" 
                       alt="logo" 
                       className="w-full h-full object-contain p-1"
                       onError={(e) => {
                           e.currentTarget.style.display = 'none';
                           e.currentTarget.nextElementSibling?.classList.remove('hidden');
                       }}
                    />
                    <Hammer size={20} className="fill-current hidden" />
                </div>
                <div>
                    <h1 className="text-xl font-black text-gray-900 leading-none tracking-tight">
                      Мастер<span className="text-primary">Про</span>
                    </h1>
                    <p className="text-[10px] font-bold text-gray-400 tracking-[0.2em] uppercase mt-0.5">
                      Сервис РФ
                    </p>
                </div>
            </div>
            
            <div className="flex items-center gap-3">
                 <div className="flex items-center gap-2 bg-white/60 backdrop-blur-md py-1.5 pl-1.5 pr-3 rounded-full border border-white/40 shadow-sm">
                    <div className="w-7 h-7 bg-gray-100 rounded-full flex items-center justify-center text-gray-400">
                        <User size={14} />
                    </div>
                    <span className="text-xs font-bold text-gray-700">Гость</span>
                 </div>
            </div>
        </div>
        
        <div className="relative">
             <div className="absolute -left-4 -top-4 w-20 h-20 bg-blue-400/10 rounded-full blur-2xl pointer-events-none"></div>
             <h2 className="text-[2rem] font-extrabold text-gray-900 leading-[1.1] relative z-10">
              Что нужно <br/> 
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-blue-600">
                починить сегодня?
              </span>
            </h2>
        </div>
      </header>

      <div className="grid grid-cols-2 gap-4 px-1">
        {SERVICE_CATEGORIES.map((cat, index) => (
          <div key={cat.id} className="animate-fadeIn" style={{ animationDelay: `${index * 0.05}s` }}>
            <CategoryCard 
                category={cat} 
                onClick={() => navigate(`/category/${cat.id}`)} 
            />
          </div>
        ))}
      </div>
      <CartButton itemCount={cartCount} onClick={onCartClick} />
    </div>
  );
};

const CategoryScreen = ({ cartCount, onCartClick }: { cartCount: number, onCartClick: () => void }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const category = SERVICE_CATEGORIES.find(c => c.id === id);

  if (!category) return <div>Категория не найдена</div>;

  const Icon = (Icons as any)[category.iconName] || Icons.HelpCircle;

  return (
    <div className="p-4 pb-24 min-h-screen bg-[#F0F2F5]">
      <div className="sticky top-0 z-20 bg-[#F0F2F5]/80 backdrop-blur-md -mx-4 px-4 py-2 mb-4">
          <div className="flex items-center py-2">
            <button 
                onClick={() => navigate('/home')} 
                className="mr-4 w-10 h-10 bg-white rounded-xl shadow-sm flex items-center justify-center text-gray-700 active:scale-90 transition-transform"
            >
              <ArrowLeft size={20} />
            </button>
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-blue-100 text-primary rounded-lg flex items-center justify-center">
                 {/* Use local icon if available */}
                 {category.iconAsset ? (
                   <img src={category.iconAsset} className="w-5 h-5 object-contain" onError={(e) => { e.currentTarget.style.display='none'; e.currentTarget.nextElementSibling?.classList.remove('hidden') }} />
                 ) : null}
                 <HelpCircle size={20} className={category.iconAsset ? 'hidden' : ''} />
              </div>
              <h2 className="text-xl font-bold text-gray-900">{category.title}</h2>
            </div>
          </div>
      </div>

      <div className="flex flex-col gap-4">
        {category.services.map((service, index) => (
          <div key={service.id} className="animate-fadeIn" style={{ animationDelay: `${index * 0.05}s` }}>
             <ServiceCard
                service={service}
                onClick={() => navigate(`/service/${category.id}/${service.id}`)}
              />
          </div>
        ))}
      </div>
      <CartButton itemCount={cartCount} onClick={onCartClick} />
    </div>
  );
};

const ServiceDetailsScreen = ({ 
  onAddToCart 
}: { 
  onAddToCart: (item: CartItem) => void 
}) => {
  const { categoryId, serviceId } = useParams();
  const navigate = useNavigate();
  const [customServiceName, setCustomServiceName] = useState('');
  
  const category = SERVICE_CATEGORIES.find(c => c.id === categoryId);
  const service = category?.services.find(s => s.id === serviceId);

  useEffect(() => {
    // Reset custom name when service changes
    setCustomServiceName('');
  }, [serviceId]);

  if (!service || !category) return <div>Услуга не найдена</div>;

  const isCustomService = service.id.startsWith('other_');
  const isNegotiable = service.minPrice === 0 && service.maxPrice === 0;

  // Since urgency is now global per order, we show base price here
  const currentPriceRange = calculatePriceRange(service, false);

  const handleAdd = () => {
    const newItem: CartItem = {
        id: Date.now().toString(),
        service: service,
        categoryName: category.title,
        customServiceName: isCustomService ? customServiceName : undefined
    };
    onAddToCart(newItem);
    navigate(`/category/${categoryId}`);
  };

  return (
    <div className="min-h-screen bg-white pb-32">
      <div className="h-64 relative overflow-hidden group">
        {/* Service Image Logic: Local -> Picsum -> Placeholder */}
        <img 
            src={service.imageAsset || `https://picsum.photos/800/600?random=${service.id}`}
            onError={(e) => {
                // If random photo fails, load placeholder
                e.currentTarget.src = "/assets/images/service_placeholder.jpg";
            }}
            className="w-full h-full object-cover"
            alt="service"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-transparent to-black/60" />
        <button 
            onClick={() => navigate(`/category/${categoryId}`)} 
            className="absolute top-4 left-4 w-10 h-10 bg-white/20 backdrop-blur-md border border-white/30 rounded-xl flex items-center justify-center text-white active:scale-90 transition-transform"
        >
          <ArrowLeft size={20} />
        </button>
        <div className="absolute bottom-6 left-4 right-4">
            <span className="inline-block px-3 py-1 bg-white/20 backdrop-blur-md rounded-lg text-white text-xs font-medium mb-2 border border-white/10">
                {category?.title}
            </span>
            <h1 className="text-3xl font-bold text-white leading-tight shadow-black drop-shadow-md">{service.name}</h1>
        </div>
      </div>

      <div className="p-5 -mt-4 bg-[#F0F2F5] rounded-t-3xl relative z-10 min-h-[50vh]">
        <div className="w-12 h-1.5 bg-gray-300 rounded-full mx-auto mb-6 opacity-50" />
        
        <div className="bg-white p-5 rounded-3xl shadow-card mb-6 relative overflow-hidden">
          <div className="absolute right-0 top-0 w-32 h-32 bg-blue-50 rounded-full -mr-10 -mt-10 blur-3xl opacity-60 pointer-events-none" />
          <p className="text-sm text-gray-400 mb-1 font-medium">Стоимость услуги</p>
          
          {isNegotiable ? (
             <div className="flex items-center gap-2 relative z-10 mt-1">
                 <div className="p-2 bg-blue-50 rounded-full text-primary">
                    <MessagesSquare size={24} />
                 </div>
                 <p className="text-xl font-extrabold text-gray-900 tracking-tight">Цену назовет мастер</p>
             </div>
          ) : (
            <div className="flex items-baseline gap-2 relative z-10">
                <p className="text-3xl font-extrabold text-gray-900 tracking-tight">
                    {formatCurrency(currentPriceRange.min)}
                </p>
                <span className="text-gray-400">–</span>
                <p className="text-xl font-bold text-gray-500">
                    {formatCurrency(currentPriceRange.max)}
                </p>
            </div>
          )}
          
          {service.unit && <p className="text-sm text-gray-400 mt-1">Цена за {service.unit}</p>}
        </div>

        {isCustomService && (
            <div className="bg-white p-4 rounded-3xl shadow-card mb-6 border border-primary/20 animate-fadeIn">
                 <label className="flex items-center gap-2 text-sm font-bold text-gray-700 mb-3">
                    <PenLine size={16} className="text-primary" />
                    Название услуги / проблемы
                 </label>
                 <input 
                    type="text"
                    autoFocus
                    placeholder="Например: Повесить турник"
                    value={customServiceName}
                    onChange={(e) => setCustomServiceName(e.target.value)}
                    className="w-full bg-gray-50 p-4 rounded-xl border-none outline-none text-base font-medium focus:bg-blue-50 focus:ring-1 focus:ring-primary/30 transition-all"
                 />
            </div>
        )}

        <div className="bg-white/50 p-4 rounded-3xl mb-4 text-center">
            <p className="text-sm text-gray-500">
               Срочность, адрес и время выезда указываются при оформлении всего заказа.
            </p>
        </div>
      </div>

      <StickyButton 
        text="Добавить в заказ" 
        disabled={isCustomService && !customServiceName} 
        onClick={handleAdd}
        secondaryText={!isNegotiable ? `~${formatCurrency((currentPriceRange.min + currentPriceRange.max)/2)}` : undefined}
      />
    </div>
  );
};

const SummaryScreen = ({ 
    orderDraft, 
    setOrderDraft, 
    onRemoveItem 
}: { 
    orderDraft: OrderDraft, 
    setOrderDraft: React.Dispatch<React.SetStateAction<OrderDraft>>,
    onRemoveItem: (id: string) => void
}) => {
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);

  if (orderDraft.items.length === 0) {
    return (
        <div className="min-h-screen bg-[#F0F2F5] p-4 flex flex-col items-center justify-center text-center">
             <div className="w-24 h-24 bg-white rounded-full flex items-center justify-center mb-6 shadow-3d">
                <ShoppingBag size={40} className="text-gray-300" />
             </div>
             <h2 className="text-xl font-bold text-gray-900 mb-2">Корзина пуста</h2>
             <p className="text-gray-500 mb-8 max-w-xs">Добавьте услуги, чтобы оформить заказ</p>
             <button onClick={() => navigate('/home')} className="bg-primary text-white py-3 px-8 rounded-xl font-bold">
                Перейти к услугам
             </button>
        </div>
    )
  }

  const cartTotal = calculateCartTotal(orderDraft.items, orderDraft.isUrgent);
  const isNegotiableTotal = cartTotal.min === 0 && cartTotal.max === 0;

  const handleUrgencyToggle = () => {
    setOrderDraft(prev => ({ ...prev, isUrgent: !prev.isUrgent }));
  };

  const handleOrder = () => {
    const tg = window.Telegram?.WebApp;
    // Prepare string summary of items
    const itemsDescription = orderDraft.items.map(i => i.customServiceName || i.service.name).join(', ');

    const orderData = {
      type: 'NEW_ORDER',
      payload: {
        items: orderDraft.items,
        totalMin: cartTotal.min,
        totalMax: cartTotal.max,
        address: orderDraft.address,
        date: orderDraft.date,
        time: orderDraft.time,
        isUrgent: orderDraft.isUrgent,
        comment: orderDraft.comment,
        description: itemsDescription,
        user: tg?.initDataUnsafe?.user,
        createdAt: new Date().toISOString()
      }
    };

    if (tg) {
      tg.sendData(JSON.stringify(orderData));
    } else {
      console.log('TELEGRAM WEBAPP DATA:', orderData);
      alert('Заказ отправлен (demo)');
      setOrderDraft(prev => ({ ...prev, items: [] })); // Clear cart
      navigate('/home');
    }
  };

  return (
    <div className="min-h-screen bg-[#F0F2F5] p-4 pb-28">
      <div className="flex items-center mb-8 mt-2">
        <button onClick={() => navigate('/home')} className="mr-4 w-10 h-10 bg-white rounded-xl shadow-sm flex items-center justify-center text-gray-700 active:scale-90 transition-transform">
          <ArrowLeft size={20} />
        </button>
        <h1 className="text-2xl font-bold text-gray-900">Ваш заказ <span className="text-gray-400 text-lg font-medium ml-2">({orderDraft.items.length})</span></h1>
      </div>

      {/* Cart Items List */}
      <div className="space-y-3 mb-8">
        {orderDraft.items.map((item) => {
            const price = calculatePriceRange(item.service, orderDraft.isUrgent);
            const isItemNegotiable = item.service.minPrice === 0;
            return (
                <div key={item.id} className="bg-white p-4 rounded-3xl shadow-card flex items-center justify-between">
                    <div className="flex-1 pr-2">
                        <p className="text-xs text-gray-400 font-bold uppercase tracking-wider mb-1">{item.categoryName}</p>
                        <h4 className="font-bold text-gray-900 mb-1">{item.customServiceName || item.service.name}</h4>
                        <p className="text-sm text-primary font-medium">
                            {isItemNegotiable ? 'Цена договорная' : `${formatCurrency(price.min)} – ${formatCurrency(price.max)}`}
                        </p>
                    </div>
                    <button 
                        onClick={() => onRemoveItem(item.id)}
                        className="w-10 h-10 bg-red-50 text-red-500 rounded-xl flex items-center justify-center hover:bg-red-100 transition-colors"
                    >
                        <Trash2 size={18} />
                    </button>
                </div>
            )
        })}
      </div>

      {/* Order Settings */}
      <div className="space-y-4 mb-8">
        <h2 className="text-lg font-bold text-gray-900 ml-1">Детали выезда</h2>
        
        {/* Urgency Toggle */}
        <div 
          onClick={handleUrgencyToggle}
          className={`flex items-center justify-between p-4 rounded-3xl shadow-card cursor-pointer active:scale-[0.99] transition-all border-2 ${orderDraft.isUrgent ? 'bg-orange-50 border-orange-200' : 'bg-white border-transparent'}`}
        >
          <div className="flex items-center space-x-3">
            <div className={`w-10 h-10 rounded-2xl flex items-center justify-center transition-colors duration-300 ${orderDraft.isUrgent ? 'bg-orange-500 text-white shadow-lg shadow-orange-200' : 'bg-gray-100 text-gray-400'}`}>
              <Icons.Zap size={20} className={orderDraft.isUrgent ? 'fill-current' : ''} />
            </div>
            <div>
              <p className={`font-bold ${orderDraft.isUrgent ? 'text-orange-900' : 'text-gray-900'}`}>Срочный выезд</p>
              <p className="text-xs text-gray-500 font-medium">+30% к стоимости работ</p>
            </div>
          </div>
          <div className={`w-14 h-8 rounded-full relative transition-all duration-300 ${orderDraft.isUrgent ? 'bg-orange-500' : 'bg-gray-200'}`}>
            <div className={`absolute top-1 w-6 h-6 bg-white rounded-full shadow-md transition-all duration-300 cubic-bezier(0.4, 0, 0.2, 1) ${orderDraft.isUrgent ? 'left-7' : 'left-1'}`} />
          </div>
        </div>

        <div className="bg-white p-4 rounded-3xl shadow-card">
            <label className="flex items-center gap-2 text-sm font-bold text-gray-700 mb-3">
                <Icons.MapPin size={16} className="text-primary" />
                Адрес
            </label>
            <AddressAutocomplete 
              value={orderDraft.address} 
              onChange={(value) => setOrderDraft({...orderDraft, address: value})} 
            />
        </div>

        <div className="grid grid-cols-2 gap-3">
            <div className="bg-white p-4 rounded-3xl shadow-card">
              <label className="block text-xs font-bold text-gray-400 mb-2 uppercase tracking-wide">Дата</label>
              <input 
                  type="date" 
                  min={new Date().toISOString().split('T')[0]}
                  value={orderDraft.date}
                  onChange={(e) => setOrderDraft({...orderDraft, date: e.target.value})}
                  disabled={orderDraft.isUrgent}
                  className={`w-full text-sm font-semibold outline-none bg-transparent text-gray-800 ${orderDraft.isUrgent ? 'opacity-40' : ''}`}
                />
            </div>
            <div className="bg-white p-4 rounded-3xl shadow-card">
              <label className="block text-xs font-bold text-gray-400 mb-2 uppercase tracking-wide">Время</label>
              <input 
                  type="time" 
                  value={orderDraft.time}
                  onChange={(e) => setOrderDraft({...orderDraft, time: e.target.value})}
                  disabled={orderDraft.isUrgent}
                  className={`w-full text-sm font-semibold outline-none bg-transparent text-gray-800 ${orderDraft.isUrgent ? 'opacity-40' : ''}`}
                />
            </div>
        </div>

        <div className="bg-white p-4 rounded-3xl shadow-card">
             <label className="flex items-center gap-2 text-sm font-bold text-gray-700 mb-2">
                <Icons.MessageSquare size={16} className="text-primary" />
                Комментарий к заказу
             </label>
             <textarea 
                placeholder="Код домофона, детали..."
                rows={2}
                value={orderDraft.comment}
                onChange={(e) => setOrderDraft({...orderDraft, comment: e.target.value})}
                className="w-full bg-gray-50 p-3 rounded-xl border-none outline-none text-sm resize-none focus:bg-blue-50 transition-colors"
             />
        </div>
      </div>

      <div className="bg-gray-900 rounded-[2rem] p-6 shadow-2xl text-white relative overflow-hidden mb-4">
         <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -mr-20 -mt-20 blur-3xl pointer-events-none" />
         
         <div className="flex justify-between items-end mb-2 relative z-10">
            <span className="text-gray-400 font-medium">Итого</span>
            <div className="text-right">
                <span className="block text-3xl font-bold tracking-tight">
                    {isNegotiableTotal ? 'Договорная' : `~ ${formatCurrency(cartTotal.min)}`}
                </span>
                {!isNegotiableTotal && (
                    <span className="text-sm text-gray-400">до {formatCurrency(cartTotal.max)}</span>
                )}
            </div>
         </div>
         <div className="h-px bg-white/10 my-3" />
         <p className="text-xs text-gray-400 text-center relative z-10">
            Оплата производится мастеру после выполнения работ
         </p>
      </div>

      <StickyButton 
        text="Подтвердить заказ" 
        disabled={!orderDraft.address}
        onClick={() => setIsModalOpen(true)}
      />

      <ConfirmationModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onConfirm={handleOrder}
        data={{
            serviceName: `Услуг: ${orderDraft.items.length}`,
            address: orderDraft.address,
            date: orderDraft.date,
            time: orderDraft.time,
            isUrgent: orderDraft.isUrgent
        }}
      />
    </div>
  );
};

// --- App Root ---

const App: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [orderDraft, setOrderDraft] = useState<OrderDraft>({
    items: [],
    isUrgent: false,
    comment: '',
    address: '',
    date: '',
    time: '',
    files: []
  });

  const location = useLocation();
  const navigate = useNavigate();

  // Handle Initial Navigation Logic
  useEffect(() => {
    if (window.Telegram?.WebApp) {
      window.Telegram.WebApp.ready();
      window.Telegram.WebApp.expand();
      const headerColor = location.pathname === '/' ? '#ffffff' : '#F0F2F5';
      window.Telegram.WebApp.setHeaderColor(headerColor);
      window.Telegram.WebApp.setBackgroundColor(headerColor);
    }

    // Force redirect on app mount (simulating a fresh app start)
    // This fixes the issue where browser address bar remembers deep links like '.../kitchen'
    const hasSeen = localStorage.getItem('hasSeenOnboarding');
    
    // Only run this logic ONCE on mount (empty dependency array)
    if (hasSeen) {
       // If user is returning, always bring them to Home
       navigate('/home', { replace: true });
    } else {
       // If user is new, make sure they are at root
       if (location.pathname !== '/') {
          navigate('/', { replace: true });
       }
    }
  }, []); // Intentionally empty to run once on mount

  const addToCart = (item: CartItem) => {
    setOrderDraft(prev => ({
        ...prev,
        items: [...prev.items, item]
    }));
  };

  const removeFromCart = (id: string) => {
    setOrderDraft(prev => ({
        ...prev,
        items: prev.items.filter(i => i.id !== id)
    }));
  };

  if (loading) {
    return <SplashScreen onComplete={() => setLoading(false)} />;
  }

  return (
    <Routes>
      <Route path="/" element={<WelcomeScreen />} />
      <Route 
        path="/home" 
        element={
            <HomeScreen 
                cartCount={orderDraft.items.length} 
                onCartClick={() => navigate('/summary')}
            />
        } 
      />
      <Route 
        path="/category/:id" 
        element={
            <CategoryScreen 
                cartCount={orderDraft.items.length} 
                onCartClick={() => navigate('/summary')}
            />
        } 
      />
      <Route 
          path="/service/:categoryId/:serviceId" 
          element={<ServiceDetailsScreen onAddToCart={addToCart} />} 
      />
      <Route 
          path="/summary" 
          element={
            <SummaryScreen 
                orderDraft={orderDraft} 
                setOrderDraft={setOrderDraft} 
                onRemoveItem={removeFromCart}
            />
          } 
      />
    </Routes>
  );
};

export default () => (
    <HashRouter>
        <App />
    </HashRouter>
);

declare global {
  interface Window {
    Telegram: any;
  }
}